import React, { useEffect, useRef, useState } from 'react'
import { Col, Form, Select as SelectAntd } from 'antd'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/Ant'
import { Contract, CostCenter, RequestStandardSharing } from 'src/types'
import { ApplicationState } from 'src/store'
import {
  getInitialValues,
  getValidationSchema,
  validate
} from 'src/core/forms/request/sharing'
import { useStepsContext } from 'src/views/Requests/views/Form'
import { useT } from 'src/core/hooks/use-translation'
import StepsNavigation from 'src/views/Requests/views/Form/components/StepsNavigation'
import useCalcRestPercentage from 'src/core/hooks/use-calc-rest-percentage'
import useStandardResource from 'src/core/hooks/use-standard-resource'

import { WrapButtons } from './styles'
import FormFields from './components/FormFields'
import SharingSummary from './components/SharingSummary'

import { requestStandardSharingsReduce } from '../Budget/budget-summary'
import { useSegmentControlContext } from '../..'
import { WrapCard, WrapRow } from '../../../styles'
import BudgetCard from '../Budget/budget-card'
import SegmentControl from '../SegmentControl'

function getCostCenter(
  userCostCenter: CostCenter,
  standardCostCenter: CostCenter
): CostCenter {
  if (userCostCenter?.cost_center_id !== '') {
    return userCostCenter
  }
  if (standardCostCenter?.cost_center_id !== '') {
    return standardCostCenter
  }

  return {
    cost_center_id: null,
    cost_center: null
  } as any
}

function getContract(
  userContract: Contract,
  standardContract: Contract
): Contract {
  if (userContract?.contract_id !== '') {
    return userContract
  }
  if (standardContract?.contract_id !== '') {
    return standardContract
  }

  return {
    contract_id: null,
    contract: null
  } as any
}

export default function Sharing(): JSX.Element {
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))
  const { t } = useT()
  const costCenterStandard = useStandardResource('cost_centers')
  const contractStandard = useStandardResource('contracts')

  const stepsContext = useStepsContext()
  const segmentControlContext = useSegmentControlContext()

  const restPercentage = useCalcRestPercentage(
    stepsContext.request.request_standard_assignments.request_standard_sharings
  )
  const [incompleteSharing, setIncompleteSharing] = useState<boolean>(false)

  const refCostCenterIdSelect = useRef<SelectAntd<string>>(null)
  const refFormik = useRef(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!refFormik.current) {
        return
      }

      setIncompleteSharing(false)
      refFormik.current.setFieldValue('percentage', restPercentage)
    }, 200)
    return () => {
      clearTimeout(timeout)
    }
  }, [restPercentage])

  useEffect(() => {
    segmentControlContext.setDisabled('billing', restPercentage > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restPercentage])

  const userCostCenter = stepsContext.request.request_user_data[0]?.cost_center
  const userContract = me.user.contract

  const initialValues = getInitialValues(
    getCostCenter(userCostCenter, costCenterStandard),
    getContract(userContract, contractStandard)
  )

  function handleResetForm() {
    const cost_center = getCostCenter(userCostCenter, costCenterStandard)
    const contract = getContract(userContract, contractStandard)

    setTimeout(() => {
      refFormik.current.resetForm({
        ...initialValues,
        contract_id: contract?.contract_id,
        contract,
        cost_center,
        cost_center_id: cost_center?.cost_center_id
      })
    }, 100)
  }

  return (
    <Formik
      ref={refFormik}
      initialValues={{ ...initialValues }}
      onSubmit={values => {
        const request_standard_sharings = [
          ...stepsContext.request.request_standard_assignments.request_standard_sharings.filter(
            ({ request_standard_sharing_id, tmp_id }) =>
              tmp_id !== values.tmp_id ||
              request_standard_sharing_id !== values.request_standard_sharing_id
          ),
          {
            ...values,
            ...(values.request_standard_sharing_id
              ? {}
              : { tmp_id: Math.random() })
          }
        ]

        const updatedRequest = {
          ...stepsContext.request,
          request_standard_assignments: {
            ...stepsContext.request.request_standard_assignments,
            request_standard_sharings
          }
        }

        const budgets = request_standard_sharings.reduce(
          requestStandardSharingsReduce,
          []
        )

        stepsContext.setBudgetAllocations({
          budgets
        })
        stepsContext.setRequest(updatedRequest)

        handleResetForm()
        refCostCenterIdSelect.current.focus()
      }}
      validate={values => {
        validate({ request: stepsContext.request, t, values })
      }}
      validationSchema={getValidationSchema()}
      render={formikProps => {
        const isTempId =
          !!formikProps.values.request_standard_sharing_id ||
          !!formikProps.values.tmp_id
        return (
          <Form onSubmit={formikProps.handleSubmit}>
            <StepsNavigation />
            {/* <SegmentControl /> */}
            <WrapRow>
              <Col span={15}>
                <WrapCard
                  bodyStyle={{ minHeight: 230 }}
                  title={<SegmentControl />}
                >
                  <FormFields
                    formikProps={formikProps}
                    organization_id={stepsContext.request.organization_id}
                    refCostCenterIdSelect={refCostCenterIdSelect}
                  />
                  <Form.Item>
                    <WrapButtons>
                      <Button
                        block
                        data-test-id="sharing-action"
                        htmlType="submit"
                        type="primary"
                        icon={isTempId ? 'edit' : ''}
                        title={
                          isTempId
                            ? 'common.actions.change'
                            : 'common.actions.save'
                        }
                      />
                      {isTempId && (
                        <div>
                          <Button
                            onClick={() => handleResetForm()}
                            type="dashed"
                            title="common.actions.cancel-edit"
                          />
                        </div>
                      )}
                    </WrapButtons>
                  </Form.Item>
                </WrapCard>
              </Col>
              <Col offset={1} span={8}>
                {stepsContext.budgetChartAllocations.active && <BudgetCard />}
                <SharingSummary
                  bodyStyle={{ maxHeight: 400, overflow: 'auto' }}
                  handleDelete={(itemDelete: RequestStandardSharing) => {
                    const { request_standard_sharing_id } = itemDelete

                    const request_standard_sharings =
                      request_standard_sharing_id
                        ? stepsContext.request.request_standard_assignments.request_standard_sharings.map(
                            request_standard_sharing =>
                              request_standard_sharing.request_standard_sharing_id ===
                              request_standard_sharing_id
                                ? { ...request_standard_sharing, active: false }
                                : request_standard_sharing
                          )
                        : stepsContext.request.request_standard_assignments.request_standard_sharings.filter(
                            ({ tmp_id }) => tmp_id !== itemDelete.tmp_id
                          )

                    const updatedRequest = {
                      ...stepsContext.request,
                      request_standard_assignments: {
                        ...stepsContext.request.request_standard_assignments,
                        request_standard_sharings
                      }
                    }

                    const budgets = request_standard_sharings.reduce(
                      requestStandardSharingsReduce,
                      []
                    )

                    stepsContext.setBudgetAllocations({
                      budgets
                    })

                    stepsContext.setRequest(updatedRequest)

                    handleResetForm()
                  }}
                  handleEdit={itemEdit => formikProps.resetForm(itemEdit)}
                  incompleteSharing={incompleteSharing}
                  request={stepsContext.request}
                  restPercentage={restPercentage}
                />
              </Col>
            </WrapRow>
          </Form>
        )
      }}
    />
  )
}
